/* assets/quick-shop.js */

(() => {
  if (window.quickShopInitialized) return;
  window.quickShopInitialized = true;

  let currentAbortController = null;

  /**
   * Resets a Quick Shop modal's content back to the loading state.
   * @param {Element} modal - modal-dialog element.
   */
  function resetModalContent(modal) {
    const inner = modal.querySelector('[data-quick-shop-inner]');
    if (inner) inner.innerHTML = '<div class="loading-spinner"></div>';
  }

  /**
   * Adapts a cloned .quickbuy-content block (the PDP's product-form) for use
   * inside the Quick Shop modal. This is a standalone copy of the same DOM
   * surgery theme.quickBuy performs on main-product's quickbuy-content — kept
   * separate so this feature doesn't depend on theme.settings.quickbuyStyle
   * (theme.quickBuy is only defined when that setting isn't 'off').
   *
   * Also registered below as theme.quickBuy.convertToQuickBuyContent (when
   * that isn't already defined) — variant-picker.js has a built-in hook that
   * calls exactly that function to re-sanitize freshly-fetched markup every
   * time a variant or combined-listing option changes INSIDE an already-open
   * quick-view container. Without this registration, that hook is a no-op
   * (since theme.quickBuy doesn't exist while the sitewide feature is off),
   * so every in-modal variant change re-introduced unsuffixed ids/names that
   * collided with the real page all over again — this function only ran
   * once, at initial modal load.
   * @param {Element} container - Cloned .quickbuy-content element (or, on
   *   subsequent variant changes, the smaller replacement fragment).
   * @param {string} productUrl - Canonical product URL.
   */
  function convertToQuickShopContent(container, productUrl) {
    // Critical: without this class, variant-picker.js's constructor
    // (`this.closest('.js-quickbuy')`) can't tell it's inside an isolated
    // quick-view clone, and falls back to `this.closest('.shopify-section')` —
    // which resolves to the REAL underlying page's own section, since every
    // product page shares the same literal section id. That caused variant
    // changes inside the modal to overwrite the real page's title/price/ATC
    // hidden input instead of the modal's own copy.
    container.classList.add('js-quickbuy');

    // variant-picker.js reads this back via `this.quickBuyContainer.dataset.productUrl`
    // on every subsequent in-modal variant change, to re-invoke this function.
    container.dataset.productUrl = productUrl;

    container.querySelectorAll('.more').forEach((el) => { el.href = productUrl; });
    container.querySelectorAll('.product-title').forEach((el) => {
      el.innerHTML = `<a href="">${el.innerHTML}</a>`;
      el.firstElementChild.href = productUrl;
    });
    container.querySelectorAll('.show-gallery').forEach((el) => {
      el.classList.remove('show-gallery');
      el.href = productUrl;
    });
    container.querySelectorAll('.not-in-quickbuy').forEach((el) => { el.remove(); });
    container.querySelectorAll('.only-in-quickbuy').forEach((el) => { el.classList.remove('only-in-quickbuy'); });
    container.querySelectorAll('.sticky-content-container').forEach((el) => { el.classList.remove('sticky-content-container'); });
    container.querySelectorAll('pickup-availability').forEach((el) => { el.remove(); });
    container.querySelectorAll('variant-picker[data-update-url="true"]').forEach((el) => { el.dataset.updateUrl = false; });

    if (typeof theme !== 'undefined' && typeof theme.suffixIds === 'function') {
      theme.suffixIds(container, 'QuickShop');
    }

    // The variant option radios (`name="{option}-option"`) have no `form`
    // attribute and aren't nested in a <form>, so per the HTML spec they have
    // no form owner. Radios with no form owner that share a `name` are
    // grouped natively by the browser across the ENTIRE document — so without
    // this, picking an option in the modal silently un-checks the real page's
    // own radio in the same group. theme.suffixIds() intentionally never
    // touches `name` (it's also used on the real cart form's critical fields
    // like name="id" and name="quantity", which must stay exact), so this is
    // handled separately and scoped to only the option inputs.
    container.querySelectorAll('.js-option[name]').forEach((el) => {
      if (!el.name.endsWith('QuickShop')) el.name += 'QuickShop';
    });

    // Enforce thumbnails-under gallery layout, same as sitewide quick buy.
    container.querySelectorAll('.media-gallery .main-image carousel-slider').forEach((el) => { el.classList.remove('mobile-only'); });
    container.querySelectorAll('.product-media-collage').forEach((el) => { el.remove(); });
    container.querySelectorAll('.media-gallery .thumbnails').forEach((el) => { el.classList.remove('mobile-only'); });
    [
      'media-gallery--layout-carousel-beside',
      'media-gallery--layout-columns-1',
      'media-gallery--layout-columns-2',
      'media-gallery--layout-collage-1',
      'media-gallery--layout-collage-2'
    ].forEach((cl) => {
      container.querySelectorAll(`.${cl}`).forEach((el) => {
        el.classList.remove(cl);
        el.classList.add('media-gallery--layout-carousel-under');
      });
    });
  }

  // Registers our sanitizer under the exact hook variant-picker.js already
  // calls (`theme.quickBuy.convertToQuickBuyContent`) for re-sanitizing
  // in-place variant swaps inside a quick-view container. Guarded so this
  // never overwrites the real sitewide quick-buy feature if that setting is
  // ever turned on — in that case theme.quickBuy is defined by main.js
  // instead, and this registration is skipped.
  if (typeof theme !== 'undefined' && typeof theme.quickBuy === 'undefined') {
    theme.quickBuy = {
      convertToQuickBuyContent: convertToQuickShopContent
    };
  }

  /**
   * Fetches a product page and injects its quickbuy-content into the modal.
   * @param {string} productUrl - Canonical product URL.
   * @param {Element} modal - modal-dialog element.
   */
  function loadQuickShop(productUrl, modal) {
    const inner = modal.querySelector('[data-quick-shop-inner]');
    if (!inner || !productUrl) return;

    if (currentAbortController) {
      currentAbortController.abort('Existing request not needed');
    }
    currentAbortController = new AbortController();

    inner.innerHTML = '<div class="loading-spinner"></div>';

    fetch(productUrl, {
      method: 'get',
      signal: currentAbortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((response) => {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = response;
        const content = tmpl.content.querySelector('.quickbuy-content');

        if (!content) {
          throw new Error('No .quickbuy-content found on fetched product page');
        }

        const newDetail = content.cloneNode(true);

        // Bring along size guide sections, same handling as sitewide quick buy.
        tmpl.content.querySelectorAll('.section-size-guide').forEach((el) => {
          if (el.querySelector('.modal--for-size-guide')) {
            newDetail.insertAdjacentElement('beforeend', el.cloneNode(true));
          } else {
            const template = el.querySelector('[data-modal-template]');
            if (template) {
              const sizeGuide = template.content.querySelector('size-guide').cloneNode(true);
              const sizeGuideContent = el.querySelector('.size-guide__content');
              if (sizeGuideContent) {
                sizeGuide.querySelector('.modal__content').appendChild(sizeGuideContent);
              }
              newDetail.insertAdjacentElement('beforeend', sizeGuide);
            }
          }
        });

        convertToQuickShopContent(newDetail, productUrl);

        // Bring along any stylesheets/scripts the product-form or size guide need.
        tmpl.content.querySelectorAll(':is(.section-main-product, .section-size-guide) link[rel="stylesheet"]').forEach((el) => {
          if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
            document.head.insertAdjacentHTML('beforeend', el.outerHTML);
          }
        });
        tmpl.content.querySelectorAll(':is(.section-main-product, .section-size-guide) script[src]').forEach((el) => {
          if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
            const scr = document.createElement('script');
            scr.src = el.getAttribute('src');
            scr.defer = 'defer';
            document.head.appendChild(scr);
          }
        });

        inner.innerHTML = '';
        inner.appendChild(newDetail);
      })
      .catch((error) => {
        if (error === 'Existing request not needed' || error?.name === 'AbortError') return;
        console.warn(error); // eslint-disable-line no-console
        inner.innerHTML = '<p class="quick-shop-modal__error">Sorry, this item can not be loaded right now.</p>';
      });
  }

  theme.addDelegateEventListener(document, 'click', '[data-quick-shop-trigger]', (evt, delEl) => {
    evt.preventDefault();

    const modal = document.getElementById(delEl.dataset.quickShopModal);
    if (!modal) return;

    // Lazily attach a reset-on-close observer, once per modal instance.
    if (!modal.dataset.quickShopObserverAttached) {
      modal.dataset.quickShopObserverAttached = 'true';
      const observer = new MutationObserver(() => {
        if (!modal.hasAttribute('open')) resetModalContent(modal);
      });
      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }

    modal.open(delEl);
    loadQuickShop(delEl.dataset.productUrl, modal);
  });
})();
